import * as React from "react"

export function formatRelativeOrDate(iso: string, now: number): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return iso
  const diffMs = now - then
  const diffMin = Math.floor(diffMs / 60_000)
  if (diffMin < 1) return "just now"
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay < 30) return `${diffDay}d ago`
  const diffMo = Math.floor(diffDay / 30)
  if (diffMo < 12) return `${diffMo} month${diffMo === 1 ? "" : "s"} ago`
  return new Date(iso).toLocaleDateString(undefined, { month: "short", year: "numeric" })
}

/**
 * Ticks every 60s for recent timestamps, stops once the value crosses 24h.
 * For older timestamps returns a stable computed value.
 */
export function useRelativeTime(iso: string): string {
  const [now, setNow] = React.useState(() => Date.now())
  React.useEffect(() => {
    const then = new Date(iso).getTime()
    if (Number.isNaN(then)) return
    const diffMs = Date.now() - then
    if (diffMs >= 24 * 60 * 60_000) return // already "Xd ago" or older — no need to tick
    const id = setInterval(() => {
      const n = Date.now()
      setNow(n)
      if (n - then >= 24 * 60 * 60_000) clearInterval(id)
    }, 60_000)
    return () => clearInterval(id)
  }, [iso])
  return formatRelativeOrDate(iso, now)
}

export function parseIsoMs(iso: string | null | undefined): number | null {
  if (!iso) return null
  const t = Date.parse(iso)
  return Number.isFinite(t) ? t : null
}

function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Compact audit timestamp for version rails: time today, date + time otherwise.
 * e.g. "12:26" or "19 Aug, 12:26" (locale-shaped).
 */
export function formatCompactLocalDateTime(iso: string, now: number): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return iso;
  const nowDate = new Date(now);
  if (isSameLocalDay(then, nowDate)) {
    return then.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  }
  return then.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    ...(then.getFullYear() !== nowDate.getFullYear() ? { year: "numeric" } : {}),
  });
}

/** Viewer-local date + time (e.g. "Apr 23, 2026, 9:00 AM"). */
export function formatFullLocalDateTime(iso: string): string | null {
  const t = parseIsoMs(iso)
  if (t == null) return null
  return new Date(t).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
}

/** mm:ss, or h:mm:ss when the run exceeds one hour. */
export function formatDurationMs(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const hh = Math.floor(totalSeconds / 3600)
  const mm = Math.floor((totalSeconds % 3600) / 60)
  const ss = totalSeconds % 60
  if (hh > 0) {
    return `${hh}:${mm.toString().padStart(2, "0")}:${ss.toString().padStart(2, "0")}`
  }
  return `${mm}:${ss.toString().padStart(2, "0")}`
}

export function getGenerationRunDurationMs(
  startedIso: string | null | undefined,
  completedIso: string | null | undefined,
  isGenerating: boolean,
  now: number,
): number | null {
  const start = parseIsoMs(startedIso)
  if (start == null) return null
  if (isGenerating) return Math.max(0, now - start)
  const end = parseIsoMs(completedIso)
  if (end == null) return null
  return Math.max(0, end - start)
}
